import { isOnPrem } from '@/utils/buildProfile';

const isDevToolsBuild = process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production';

declare global {
  interface Window {
    heidiDevTools?: {
      enable: () => void;
      disable: () => void;
    };
  }
}

// Read once: `MyApp` asks on every render, and the value only changes across a
// reload.
let cachedOptIn: boolean | undefined;

const readOptIn = (): boolean => {
  if (cachedOptIn === undefined) {
    try {
      cachedOptIn = window.localStorage.getItem('heidi.devTools.enabled') === 'true';
    } catch {
      cachedOptIn = false;
    }
  }
  return cachedOptIn;
};

// On-prem is non-production but ships to customers, so its dev tools stay off
// until someone opts in from the console with `heidiDevTools.enable()`.
export const isDevToolsEnabled = (): boolean => {
  if (!isDevToolsBuild) return false;
  if (!isOnPrem) return true;
  return typeof window !== 'undefined' && readOptIn();
};

/** Matches what a static export renders, where the on-prem opt-in is unreadable. */
export const isDevToolsEnabledOnServer = (): boolean => isDevToolsBuild && !isOnPrem;

// The flag-override providers are wired once at boot, so a toggle only takes
// effect after a reload.
const setOptIn = (enabled: boolean) => {
  try {
    if (enabled) {
      window.localStorage.setItem('heidi.devTools.enabled', 'true');
    } else {
      window.localStorage.removeItem('heidi.devTools.enabled');
    }
  } catch {
    console.warn('[Dev Tools] could not persist the setting');
    return;
  }
  cachedOptIn = enabled;
  window.location.reload();
};

export const installDevToolsConsoleToggle = () => {
  if (!isDevToolsBuild || !isOnPrem || typeof window === 'undefined') return;

  window.heidiDevTools = {
    enable: () => setOptIn(true),
    disable: () => setOptIn(false),
  };
};
